import Campaign from '@mui/icons-material/Campaign';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currencySymbolMapping } from 'src/helpers';
import { useAdvisorCatalogLoader } from './hooks';

type Props = {
  zone: string;
  testID?: string;
};

const MAX_DESCRIPTION_SIZE = 100;

export const AdvisorCatalogLoader: React.FC<Props> = ({ zone, testID = 'AdvisorCatalogLoader' }) => {
  const { fetchAdvisorCatalog, isLoading, error, data: catalog } = useAdvisorCatalogLoader();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchPosition, setTouchPosition] = useState(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (zone) {
      fetchAdvisorCatalog({ zone });
    }
  }, [zone, fetchAdvisorCatalog]);

  const handleDotClick = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleTouchStart = useCallback((e) => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (touchPosition === null) {
        return;
      }

      const currentTouch = e.touches[0].clientX;
      const diff = touchPosition - currentTouch;

      if (diff > 5) {
        // Swipe left
        setCurrentIndex((prev) => (prev < catalog.length - 1 ? prev + 1 : prev));
      } else if (diff < -5) {
        // Swipe right
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
      }

      setTouchPosition(null); // Reset touch position to prevent multiple swipes
    },
    [touchPosition, catalog.length],
  );

  if (error || isLoading) {
    return null;
  }

  return (
    <div>
      <div
        className="overflow-hidden overflow-x-scroll py-2 scrollbar-hidden"
        data-test-id={testID}
        style={{ width: '100%', maxWidth: '100vw' }}
      >
        <div className="flex flex-row space-x-2" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
          {catalog.map((item, index) => {
            if (item.availableZone) {
              return (
                <a
                  href="mailto:contact@smartcolis.com?subject=Demande%20d%27achat%20de%20zone%20publicit%C3%A9"
                  className="bg-gray-100 flex items-center justify-between rounded-lg shadow-md min-w-full p-2"
                  key={index}
                >
                  <div className="text-gray-700  text-xl md:text-2xl text-center w-full">
                    <div className="capitalize flex items-center justify-center space-x-2">
                      <div>
                        <Campaign sx={{ width: 30, height: 40, fill: '#374151' }} />
                      </div>
                      <div>{t('advertise-here')}</div>
                    </div>
                    <span className="text-sm text-gray-400"> Réservez cet espace: contact@smartcolis.com</span>
                  </div>
                </a>
              );
            }
            return (
              <a
                className="bg-gray-100 flex items-center justify-between rounded-lg shadow-md min-w-full p-4"
                href={item.url}
                rel="noreferrer noopener"
                target="_blank"
              >
                {item.fullImageOnly && item.imageUrl ? (
                  <div className="flex justify-center w-full">
                    <img src={item.imageUrl} alt={item.name} className="w-full h-auto rounded-sm shadow-sm max-h-96" />
                  </div>
                ) : (
                  <>
                    <div className="flex">
                      {!!item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-24 h-auto rounded-sm shadow-sm max-h-24 self-center"
                        />
                      )}
                      <div className="px-2 py-1">
                        <h2 className="text-md font-semibold">{item.name}</h2>
                        {!!item.description && (
                          <div className="text-gray-500 text-sm">
                            {item.description.substring(0, 100)}
                            {item.description?.length > MAX_DESCRIPTION_SIZE ? '...' : ''}
                          </div>
                        )}
                        {!!item.note && <div className="text-gray-400 text-xs mt-2">{item.note}</div>}
                        {!!item.price && (
                          <div className="text-gray-400 font-bold">
                            {`${item.price.toString().trim()} ${currencySymbolMapping(item.currency ?? 'EUR').trim()}*`}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded">
                      {t(item.ctaText ?? 'view-more')}
                    </span>
                  </>
                )}
              </a>
            );
          })}
        </div>
      </div>
      <style>
        {`
          @media (min-width: 1024px) {
        [data-test-id="${testID}"] {
          width: 100% !important;
          max-width: 100vw !important;
        }
          }
        `}
      </style>
    </div>
  );
};
